import { api, qs } from './client.js';

/** 서버: modules/serverCheck/redis.service.ts 의 RedisStatus 와 같은 모양이어야 한다. */
export interface RedisStatus {
  connected: boolean;
  error?: string;
  latencyMs?: number;
  server?: {
    version: string;
    mode: string;
    os: string;
    uptimeSeconds: number;
    tcpPort: number;
  };
  clients?: {
    connected: number;
    blocked: number;
    maxClients: number;
  };
  memory?: {
    usedBytes: number;
    usedHuman: string;
    peakHuman: string;
    maxBytes: number;
    maxmemoryPolicy: string;
    fragmentationRatio: number;
  };
  stats?: {
    totalConnectionsReceived: number;
    totalCommandsProcessed: number;
    opsPerSec: number;
    keyspaceHits: number;
    keyspaceMisses: number;
    hitRatePct: number | null;
    rejectedConnections: number;
    expiredKeys: number;
    evictedKeys: number;
  };
  persistence?: {
    rdbLastSaveTime: number;
    rdbLastBgsaveStatus: string;
    aofEnabled: boolean;
    aofLastWriteStatus: string;
  };
  replication?: {
    role: string;
    connectedSlaves: number;
  };
  keyspace: { db: string; keys: number; expires: number }[];
}

export const serverCheckApi = {
  redisStatus: () => api.get<RedisStatus>('/server-check/redis'),
};


// ── 배치관리 ────────────────────────────────────────────────────────────────
// 서버: modules/batch-admin/ 과 같은 모양이어야 한다.

export interface BatchJob {
  name: string;
  title: string;
  summary: string;
  defaultSchedule: string;
  /** SMS/LMS 를 보내는 배치인지 - 수신번호 관리 대상 */
  sends: 'sms' | 'lms' | null;
  /** 켜도 지금은 실패하는 이유. null 이면 켤 수 있다. */
  blockedReason: string | null;
  /** 켤 수는 있지만 켜기 전에 알아야 할 것 (막지 않는다). */
  caution?: string;
  schedule: string;
  enabled: boolean;
  /** crontab 에 줄이 있는지 (없으면 아직 한 번도 저장 안 함) */
  registered: boolean;
  /** 마지막 실행 결과 (xadmin_batch_run). 한 번도 안 돌았으면 null. */
  lastRun: BatchLastRun | null;
}

/** 서버: modules/batch-admin/run-store.ts 의 LastRun 과 같은 모양. */
export interface BatchLastRun {
  status: 'running' | 'succeeded' | 'failed' | 'skipped';
  scheduledFor: string;
  startedAt: string | null;
  errorMessage: string | null;
  notificationCount: number;
  writeCount: number;
}

export interface BatchList {
  root: string;
  recipientStoreReady: boolean;
  /**
   * 실행 이력 테이블(migrations/003)이 적용됐는지.
   * false 면 배치를 켜도 job 본문 전에 죽는다 - 화면이 크게 알린다.
   */
  runStoreReady: boolean;
  jobs: BatchJob[];
  /** crontab 에는 있는데 카탈로그에 없는 job */
  unknown: string[];
}

export type ListKey = 'default' | 'ads';

export interface BatchRecipient {
  /** 0 이면 DB 행이 아니라 소스 기본값이다 (테이블 적용 전) */
  id: number;
  jobName: string;
  listKey: ListKey;
  phone: string;
  label: string;
  enabled: boolean;
  updatedBy: string;
  updatedAt: string | null;
}

export interface RecipientInput {
  listKey: ListKey;
  phone: string;
  label: string;
  enabled: boolean;
}

/** 서버: modules/batch-admin/smsConfig.service.ts 의 SmsConfigView 와 같은 모양. */
export interface SmsConfig {
  apiUrl: string;
  /** 가린 API 키. 온전한 값은 서버가 내려주지 않는다. */
  apiKeyMasked: string;
  timeoutMs: number;
  /** 공통 발신번호. jobSenders 에 없는 배치가 이 번호를 쓴다. */
  sender: string;
  /** 배치 이름 → 그 배치만 따로 정한 발신번호 */
  jobSenders: Record<string, string>;
  /** 테이블에서 읽은 값인지 (false 면 소스 기본값) */
  fromStore: boolean;
  /** migrations/012 가 적용됐는지 */
  storeReady: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface SmsConfigInput {
  apiUrl: string;
  timeoutMs: number;
  sender: string;
  /** 비우면 지금 키를 그대로 둔다. */
  apiKey?: string;
}

/** 서버: modules/batch-admin/preview.service.ts 의 PreviewResult 와 같은 모양. */
export interface BatchPreview {
  job: string;
  title: string;
  /** 지금 보낸다면 나갈 문안들. 비어 있으면 보낼 것이 없다는 뜻이다. */
  messages: { label: string; text: string }[];
  scheduledFor: string;
  elapsedMs: number;
}

/** 서버: modules/batch-admin/smsLog.service.ts 와 같은 모양. */
export interface SmsLogRecipient {
  phone: string;
  ok: boolean;
  detail?: string;
}

/** 발송 한 번 = 한 줄. 수신자는 JSON 배열로 함께 들어 있다. */
export interface SmsLogEntry {
  id: number;
  jobName: string;
  kind: 'sms' | 'lms';
  sender: string;
  message: string;
  recipients: SmsLogRecipient[];
  totalCount: number;
  okCount: number;
  failCount: number;
  sentAt: string;
}

export interface SmsLogPage {
  storeReady: boolean;
  entries: SmsLogEntry[];
  hasMore: boolean;
  /** 필터와 무관한 전체 요약 */
  recent: {
    /** 오늘 발송 횟수 */
    today: number;
    /** 최근 7일 발송 횟수 */
    sends: number;
    /** 그중 한 명이라도 실패한 횟수 */
    failedSends: number;
  };
}

export const batchAdminApi = {
  list: () => api.get<BatchList>('/server-check/batch'),

  smsLog: (params: {
    job?: string;
    failedOnly?: boolean;
    /** YYYY-MM-DD */
    from?: string;
    to?: string;
    page?: number;
  }) =>
    api.get<SmsLogPage>(`/server-check/batch/sms-log${qs(params)}`),

  /** 배치를 dryRun 으로 돌려 보낼 문안을 받아온다 (발송·DB 쓰기 없음). */
  preview: (job: string) =>
    api.post<BatchPreview>(`/server-check/batch/${job}/preview`),

  smsConfig: () => api.get<SmsConfig>('/server-check/batch/sms-config'),

  saveSmsConfig: (input: SmsConfigInput) =>
    api.put<SmsConfig>('/server-check/batch/sms-config', input),

  /** 이 배치만 쓸 발신번호. 빈 값이면 공통 번호로 되돌린다. */
  saveJobSender: (job: string, sender: string) =>
    api.put<SmsConfig>(`/server-check/batch/${job}/sender`, { sender }),

  save: (jobs: { name: string; schedule: string; enabled: boolean }[]) =>
    api.put<{ saved: number }>('/server-check/batch', { jobs }),

  recipients: (job: string) =>
    api.get<{ storeReady: boolean; listKeys: ListKey[]; recipients: BatchRecipient[] }>(
      `/server-check/batch/${job}/recipients`,
    ),

  addRecipient: (job: string, input: RecipientInput) =>
    api.post<{ id: string | null }>(`/server-check/batch/${job}/recipients`, input),

  updateRecipient: (job: string, id: number, input: RecipientInput) =>
    api.put<void>(`/server-check/batch/${job}/recipients/${id}`, input),

  removeRecipient: (job: string, id: number) =>
    api.del<void>(`/server-check/batch/${job}/recipients/${id}`),
};
